import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { decodeJwtPayload } from "../helpers/jwt";
import { API } from "../config";
import { cleanUp } from "../helpers/logout";

type RolesType = string[]; // You can replace `any` with your actual type
type ProfileType = any; // You can replace `any` with your actual type
type TokenType = string | null; // You can replace `any` with your actual type

interface ProfileContextType {
  profile: ProfileType;
  roles: RolesType;
  loading: boolean;
  identifier: string;
  setIdentifier: React.Dispatch<React.SetStateAction<string>>;
  token: TokenType;
  setToken: React.Dispatch<React.SetStateAction<TokenType>>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};

// Safe JSON parsing utility function
const safeJsonParse = (jsonString: string | null, fallback: any = {}) => {
  try {
    return jsonString ? JSON.parse(jsonString) : fallback;
  } catch (e) {
    console.error('JSON parse error:', e);
    return fallback;
  }
};

export const ProfileProvider = ({ children }: {children: ReactNode}) => {
  const [roles, setRoles] = useState<RolesType>([]);
  let rawProfile: string | null = null;
  try {
    rawProfile = localStorage.getItem("profile");
  } catch (e) {
    console.error("Error reading profile from localStorage:", e);
    rawProfile = "";
  }
  const [profile, setProfile] = useState<ProfileType>(safeJsonParse(rawProfile, {}));
  let rawToken = null;
  try {
    rawToken = localStorage.getItem("token");
  } catch (e) {
    console.error("Error reading token from localStorage:", e);
  }
  const [token, setToken] = useState<TokenType>(rawToken || null);
  const [identifier, setIdentifier] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      if (!window.location.pathname.endsWith("/login")) {
        console.log("No token found, redirecting to login", window.location.href);
        window.location.href = "/login" + window.location.search;
      }
      return;
    }

    try {
      fetch(API.PROFILE, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (res?.status === 401) {
            cleanUp();
            window.location.href = "/login" + window.location.search;
          }
          return res;
        })
        .then((res) => res?.json())
        .then((data) => {
          try {
          localStorage.setItem("profile", JSON.stringify(data));
          } catch (e) {
            console.error("Error saving profile to localStorage:", e);
          }
          setProfile(data)
          const tokenData = decodeJwtPayload(token) || {};
          const id = tokenData.identifier || '';
          setRoles(tokenData.roles || []);
          setIdentifier(id);
        })
        .catch((err) => console.log("Profile fetch error:", err))
        .finally(() => setLoading(false));
    } catch (err) {
      console.error("Error in ProfileProvider:", err);
      setLoading(false);
    }
  }, [token]);

  return (
    <ProfileContext.Provider value={{ profile, roles, loading, identifier, setIdentifier, token, setToken }}>
      {children}
    </ProfileContext.Provider>
  );
};
